import { useCallback, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button, Text } from 'react-native-paper';
import type { EdgeInsets } from 'react-native-safe-area-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ArrowBackIosIcon, ArrowForwardIosIcon } from '@/assets';
import { useGetGroup } from '@/features/api/hooks/grapevine-group/useGetGroup';
import { useGetPendingMembers } from '@/features/api/hooks/grapevine-group/useGetPendingMembers';
import { useAcceptPendingGroupJoinRequest } from '@/features/api/hooks/grapevine-group-events/useAcceptPendingGroupJoinRequest';
import { useDenyPendingGroupJoinRequest } from '@/features/api/hooks/grapevine-group-events/useDenyPendingGroupJoinRequest';
import { PersonAvatar } from '@/features/media/components/avatar/PersonAvatar';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { AppRoute } from '@/navigation/types/navigation';
import { OutlinedButton } from '@/shared/components/OutlinedButton';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme, insets: EdgeInsets) =>
  StyleSheet.create({
    wrapper: {
      backgroundColor: theme.colors.surfaceContainerLow,
      paddingBottom: insets.bottom,
      flex: 1,
      paddingHorizontal: 16,
      paddingVertical: 16,
      alignItems: 'center',
      flexDirection: 'column',
      gap: 16
    },
    text: {
      textAlign: 'center'
    },
    extraMargin: {
      marginVertical: 16
    },
    group: {
      marginBottom: 8,
      gap: 16,
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'stretch'
    },
    input: {
      minHeight: 180,
      maxHeight: 180,
      alignSelf: 'stretch',
      marginBottom: 8
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-around',
      alignItems: 'center',
      width: '100%',
      gap: 24
    },
    button: {
      minWidth: 172
    }
  });

type Props = AppNativeStackScreenProps<typeof AppRoute.GroupRequests>;

export const GroupRequestsScreen: FC<Props> = ({ route, navigation }) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [insets, theme]);
  const { t } = useTranslation(LocalizationNameSpace.GROUPS, {
    keyPrefix: 'screens.requests'
  });

  const iconProps = {
    width: 24,
    height: 24
  };

  const { groupId } = route.params;
  const { data: pendingMembers } = useGetPendingMembers(groupId);
  const { data: group } = useGetGroup(groupId);

  const [displayedRequest, setDisplayedRequest] = useState(
    pendingMembers?.content?.[0]
  );

  const showNavigationIcons = (pendingMembers?.content?.length ?? 0) > 1;

  const switchDisplayedRequest = useCallback(
    (offset: number) => {
      if ((pendingMembers?.content?.length ?? 0) <= 1) return;
      setDisplayedRequest((currentRequest) => {
        const currentIndex = currentRequest
          ? (pendingMembers?.content?.findIndex(
              (r) => r.id === currentRequest.id
            ) ?? 0)
          : 0;
        const nextIndex =
          currentIndex + offset < (pendingMembers?.content?.length ?? 0)
            ? currentIndex + offset
            : 0;
        return pendingMembers?.content?.at(nextIndex);
      });
    },
    [pendingMembers?.content]
  );

  const handleRemovalOfRequest = useCallback(
    (idToRemove: string) => {
      const newPendingMembers =
        pendingMembers?.content?.filter((r) => r.id === idToRemove) ?? [];
      if (newPendingMembers?.length > 0) {
        switchDisplayedRequest(-1);
      } else {
        navigation?.popTo(AppRoute.GroupDetail, {
          groupId,
          name: group?.name,
          shortName: group?.shortName,
          memberCount: group?.memberCount
        });
      }
    },
    [
      group?.memberCount,
      group?.name,
      group?.shortName,
      groupId,
      navigation,
      pendingMembers?.content,
      switchDisplayedRequest
    ]
  );

  const acceptRequest = useAcceptPendingGroupJoinRequest(groupId);
  const handleAcceptRequest = useCallback(
    (idToAccept: string) =>
      acceptRequest.mutate(idToAccept, {
        onSuccess: () => handleRemovalOfRequest(idToAccept)
      }),
    [acceptRequest, handleRemovalOfRequest]
  );

  const denyRequest = useDenyPendingGroupJoinRequest(groupId);
  const handleDenyRequest = useCallback(
    (idToDeny: string) =>
      denyRequest.mutate(idToDeny, {
        onSuccess: () => handleRemovalOfRequest(idToDeny)
      }),
    [denyRequest, handleRemovalOfRequest]
  );

  const disableButtons = denyRequest.isPending || acceptRequest.isPending;

  return (
    displayedRequest && (
      <View style={styles.wrapper}>
        <View style={styles.row}>
          {showNavigationIcons && (
            <ArrowBackIosIcon
              {...iconProps}
              color="primary"
              onPress={() => switchDisplayedRequest(-1)}
            />
          )}
          <PersonAvatar person={displayedRequest.member} size={200} />
          {showNavigationIcons && (
            <ArrowForwardIosIcon
              {...iconProps}
              color="primary"
              onPress={() => switchDisplayedRequest(1)}
            />
          )}
        </View>
        <Text variant="headlineLarge" style={styles.text}>
          {`${displayedRequest.member?.firstName} ${displayedRequest.member?.lastName}`}
        </Text>
        {/* TODO> add geoArea Name
        <Text variant="bodyLarge" style={styles.text}>
            {displayedRequest.member?.}
        </Text> */}

        <View style={styles.extraMargin}>
          {displayedRequest?.memberIntroductionText && (
            <Text variant="bodyLarge" style={[styles.text]}>
              {`"${displayedRequest?.memberIntroductionText.trim()}"`}
            </Text>
          )}
        </View>

        {/* TODO: add NewChatOnUser */}

        <Button
          mode="contained"
          style={styles.button}
          onPress={() => handleAcceptRequest(displayedRequest.member.id)}
          disabled={disableButtons}
        >
          {t('accept')}
        </Button>
        <OutlinedButton
          style={styles.button}
          onPress={() => handleDenyRequest(displayedRequest.member.id)}
          disabled={disableButtons}
        >
          {t('deny')}
        </OutlinedButton>
      </View>
    )
  );
};
